import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { X, Send, AlertCircle, CheckCircle, Loader2, Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const DepositConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  amount,
  currency,
  depositAddress,
  sending,
  t
}) => {
  const [txHash, setTxHash] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState('input'); // 'input', 'processing', 'success'

  // Helper function to get translation with fallback
  const tr = (key, fallback) => {
    const result = t?.(key);
    return (result && result !== key) ? result : fallback;
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setTxHash('');
      setError(null);
      setStep('input');
      setCopied(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // TX Hash validation
  const isTxHashValid = txHash.trim().length >= 64 && txHash.trim().startsWith('0x');
  
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(depositAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleConfirm = async () => {
    if (!isTxHashValid) {
      setError(tr('invalidTxHash', 'Введите корректный хеш транзакции (начинается с 0x)'));
      return;
    }

    setStep('processing');
    setError(null);
    
    try {
      await onConfirm({
        type: 'manual',
        txHash: txHash.trim(),
        amount,
        currency,
      });
      setStep('success');
      // Auto close after success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message || tr('depositError', 'Ошибка при создании заявки'));
      setStep('input');
    }
  };

  const handleClose = () => {
    if (step !== 'processing') {
      setTxHash('');
      setError(null);
      setStep('input');
      onClose();
    }
  };

  const isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: isSmallScreen ? 'flex-start' : 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: isSmallScreen ? '16px' : '20px',
        overflowY: 'auto',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && step !== 'processing') handleClose();
      }}
    >
      <div
        style={{
          backgroundColor: COLORS.card,
          borderRadius: '16px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: isSmallScreen ? 'none' : '90vh',
          overflow: isSmallScreen ? 'visible' : 'auto',
          border: `1px solid ${COLORS.border}`,
          marginTop: isSmallScreen ? '20px' : 0,
          marginBottom: isSmallScreen ? '20px' : 0,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px',
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: COLORS.text }}>
            {tr('depositConfirmation', 'Подтверждение депозита')}
          </h2>
          {step !== 'processing' && (
            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
              }}
            >
              <X size={24} color={COLORS.textSecondary} />
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: isSmallScreen ? '16px' : '24px' }}>
          {step === 'success' ? (
            /* Success State */
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <CheckCircle size={64} color={COLORS.success} style={{ marginBottom: '16px' }} />
              <h3 style={{ color: COLORS.text, marginBottom: '8px' }}>
                {tr('depositRequestCreated', 'Заявка создана!')}
              </h3>
              <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
                {tr('depositPending', 'Ваш депозит обрабатывается')}
              </p>
            </div>
          ) : (
            <>
              {/* Amount Info */}
              <div style={{
                backgroundColor: COLORS.background,
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px',
                textAlign: 'center',
              }}>
                <div style={{ color: COLORS.textSecondary, fontSize: '14px', marginBottom: '4px' }}>
                  {tr('depositAmount', 'Сумма депозита')}
                </div>
                <div style={{ color: COLORS.text, fontSize: '28px', fontWeight: '700' }}>
                  {amount} {currency}
                </div>
              </div>

              {/* Deposit Address with QR */}
              <div style={{
                backgroundColor: COLORS.background,
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px',
              }}>
                <div style={{ color: COLORS.textSecondary, fontSize: '14px', marginBottom: '12px' }}>
                  {tr('sendToAddress', 'Отправьте на адрес')}:
                </div>
                
                {/* QR Code */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  marginBottom: '16px',
                  backgroundColor: '#fff',
                  padding: isSmallScreen ? '8px' : '12px',
                  borderRadius: '12px',
                  width: 'fit-content',
                  margin: '0 auto 16px',
                }}>
                  <QRCodeSVG value={depositAddress} size={isSmallScreen ? 100 : 140} level="M" />
                </div>

                {/* Address */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: COLORS.card,
                  padding: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.border}`,
                }}>
                  <code style={{ 
                    flex: 1, 
                    color: COLORS.text, 
                    fontSize: '13px',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                  }}>
                    {depositAddress}
                  </code>
                  <button
                    onClick={copyAddress}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '6px',
                      backgroundColor: copied ? `${COLORS.success}20` : COLORS.background,
                    }}
                  >
                    {copied ? <Check size={18} color={COLORS.success} /> : <Copy size={18} color={COLORS.textSecondary} />}
                  </button>
                </div>
              </div>

              {/* TX Hash Input */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  color: COLORS.text, 
                  fontSize: '14px', 
                  fontWeight: '500',
                  marginBottom: '8px',
                }}>
                  {tr('transactionHash', 'Хеш транзакции')} <span style={{ color: COLORS.danger }}>*</span>
                </label>
                <input
                  type="text"
                  value={txHash}
                  onChange={(e) => {
                    setTxHash(e.target.value);
                    setError(null);
                  }}
                  placeholder="0x..."
                  disabled={step === 'processing'}
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${error ? COLORS.danger : COLORS.border}`,
                    borderRadius: '10px',
                    color: COLORS.text,
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <p style={{ 
                  color: COLORS.textSecondary, 
                  fontSize: '12px', 
                  marginTop: '8px',
                  lineHeight: '1.4',
                }}>
                  {tr('txHashHint', 'После отправки средств, скопируйте хеш транзакции из вашего кошелька и вставьте сюда')}
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  backgroundColor: `${COLORS.danger}15`,
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: `1px solid ${COLORS.danger}30`,
                }}>
                  <AlertCircle size={18} color={COLORS.danger} />
                  <span style={{ color: COLORS.danger, fontSize: '14px' }}>{error}</span>
                </div>
              )}

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleClose}
                  disabled={step === 'processing'}
                  style={{
                    flex: 1,
                    padding: '14px',
                    backgroundColor: COLORS.background,
                    color: COLORS.text,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: step === 'processing' ? 'not-allowed' : 'pointer',
                    opacity: step === 'processing' ? 0.5 : 1,
                  }}
                >
                  {tr('cancel', 'Отмена')}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!isTxHashValid || step === 'processing'}
                  style={{
                    flex: 1,
                    padding: '14px',
                    backgroundColor: isTxHashValid ? COLORS.primary : COLORS.textSecondary,
                    color: '#000',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: (!isTxHashValid || step === 'processing') ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  {step === 'processing' ? (
                    <>
                      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      {tr('processing', 'Обработка...')}
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      {tr('confirm', 'Подтвердить')}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DepositConfirmationModal;
